/**
 * Copyright (C) 2026 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

/**
 * Proto counterpart: vnis.v2.common.TimestampedPositionAndVelocity.position (oneof cases).
 * DTO counterpart: OrbitPointDto.position (OrbitPositionDto implemented by LocationDto/EcefLocationDto).
 */
export enum PositionType {
  LAT_LON_HEIGHT = 'LAT_LON_HEIGHT',
  ECEF_POSITION = 'ECEF_POSITION',
}

/**
 * Proto counterpart: vnis.v2.halonet.GroundStationType.
 * DTO counterpart: GroundStationDataDto.type and GroundStationReferenceDto.stationType.
 */
export enum GroundStationType {
  GROUND_STATION_TYPE_UNSPECIFIED = 'GROUND_STATION_TYPE_UNSPECIFIED',
  GROUND_STATION_TYPE_RTE = 'GROUND_STATION_TYPE_RTE',
}

export const GroundStationTypeDisplayLabel: Record<GroundStationType, string> = {
  [GroundStationType.GROUND_STATION_TYPE_UNSPECIFIED]: 'Unspecified',
  [GroundStationType.GROUND_STATION_TYPE_RTE]: 'RTE',
};

/**
 * Proto counterpart: vnis.v2.halonet.ReservationStatusType.
 * DTO counterpart: ReservationDto.status.
 */
export enum ReservationStatusType {
  RESERVATION_STATUS_TYPE_UNSPECIFIED = 'RESERVATION_STATUS_TYPE_UNSPECIFIED',
  RESERVATION_STATUS_TYPE_ACCEPTED = 'RESERVATION_STATUS_TYPE_ACCEPTED',
  RESERVATION_STATUS_TYPE_FAILED = 'RESERVATION_STATUS_TYPE_FAILED',
  RESERVATION_STATUS_TYPE_SCHEDULED = 'RESERVATION_STATUS_TYPE_SCHEDULED',
  RESERVATION_STATUS_TYPE_PENDING = 'RESERVATION_STATUS_TYPE_PENDING',
  RESERVATION_STATUS_TYPE_IN_PROGRESS = 'RESERVATION_STATUS_TYPE_IN_PROGRESS',
  RESERVATION_STATUS_TYPE_COMPLETED = 'RESERVATION_STATUS_TYPE_COMPLETED',
  RESERVATION_STATUS_TYPE_CANCELLED = 'RESERVATION_STATUS_TYPE_CANCELLED',
}

export const ReservationStatusTypeDisplayLabel: Record<ReservationStatusType, string> = {
  [ReservationStatusType.RESERVATION_STATUS_TYPE_UNSPECIFIED]: 'Unspecified',
  [ReservationStatusType.RESERVATION_STATUS_TYPE_ACCEPTED]: 'Accepted',
  [ReservationStatusType.RESERVATION_STATUS_TYPE_FAILED]: 'Failed',
  [ReservationStatusType.RESERVATION_STATUS_TYPE_SCHEDULED]: 'Scheduled',
  [ReservationStatusType.RESERVATION_STATUS_TYPE_PENDING]: 'Pending',
  [ReservationStatusType.RESERVATION_STATUS_TYPE_IN_PROGRESS]: 'In Progress',
  [ReservationStatusType.RESERVATION_STATUS_TYPE_COMPLETED]: 'Completed',
  [ReservationStatusType.RESERVATION_STATUS_TYPE_CANCELLED]: 'Cancelled',
};

/**
 * Proto counterpart: vnis.v2.common.Location (lat_lon_height position).
 * DTO counterpart: LocationDto.
 */
export interface LatLonHeightPosition {
  positionType: PositionType.LAT_LON_HEIGHT;
  latitude: number;
  longitude: number;
  altitudeMeters: number;
  name?: string;
}

/**
 * Proto counterpart: vnis.v2.common.EcefLocation (ecef_position).
 * DTO counterpart: EcefLocationDto.
 */
export interface EcefPosition {
  positionType: PositionType.ECEF_POSITION;
  xMeters: number;
  yMeters: number;
  zMeters: number;
  name?: string;
}

/**
 * Proto counterpart: vnis.v2.common.TimestampedPositionAndVelocity.position oneof.
 * DTO counterpart: OrbitPositionDto (LocationDto | EcefLocationDto).
 */
export type OrbitPosition = LatLonHeightPosition | EcefPosition;

/**
 * Proto counterpart: derived from vnis.v2.common.Location / EcefLocation.
 * DTO counterpart: normalized from LocationDto and EcefLocationDto.
 */
export interface GeoLocation {
  latitude: number;
  longitude: number;
  altitudeFeet: number;
  name?: string;
}

/**
 * Proto counterpart: vnis.v2.halonet.GroundStationData.
 * DTO counterpart: GroundStationDataDto.
 */
export interface GroundStationDataDto {
  groundStationName: string;
  location: LatLonHeightPosition;
  type: GroundStationType;
  downlinkOnly: boolean;
}

/**
 * Proto counterpart: vnis.v2.halonet.GroundStationData.
 * DTO counterpart: normalized GroundStationDataDto.
 */
export interface GroundStationData {
  groundStationName: string;
  location: GeoLocation;
  type: GroundStationType;
  downlinkOnly: boolean;
}

/**
 * Proto counterpart: vnis.v2.common.TimeWindow.
 * DTO counterpart: TimeWindowDto.
 */
export interface TimeWindowDto {
  start: string;
  end: string;
}

/**
 * Proto counterpart: vnis.v2.common.TimeWindow.
 * DTO counterpart: normalized TimeWindowDto.
 */
export interface TimeWindow {
  start: Date;
  end: Date;
}

/**
 * Proto counterpart: vnis.v2.halonet.GroundStationReference.
 * DTO counterpart: GroundStationReferenceDto.
 */
export interface GroundStationReferenceDto {
  stationName: string;
  stationType: GroundStationType;
}

/**
 * Proto counterpart: vnis.v2.halonet.GroundStationReference.
 * DTO counterpart: normalized GroundStationReferenceDto.
 */
export interface GroundStationReference {
  stationName: string;
  stationType: GroundStationType;
}

/**
 * Proto counterpart: vnis.v2.halonet.GroundStationAvailability.
 * DTO counterpart: GroundStationAvailabilityDto.
 */
export interface GroundStationAvailabilityDto {
  groundStation: GroundStationReferenceDto;
  svName: string;
  availabilityWindows: TimeWindowDto[];
}

/**
 * Proto counterpart: vnis.v2.halonet.GroundStationAvailability.
 * DTO counterpart: normalized GroundStationAvailabilityDto.
 */
export interface GroundStationAvailability {
  groundStation: GroundStationReference;
  svName: string;
  availabilityWindows: TimeWindow[];
}

/**
 * Proto counterpart: vnis.v2.halonet.Reservation.
 * DTO counterpart: ReservationDto.
 */
export interface ReservationDto {
  reservationId: string;
  svName: string;
  groundStation: GroundStationReferenceDto;
  timeWindow: TimeWindowDto;
  status: string;
  prepassSeconds?: number;
  postpassSeconds?: number;
  configuration?: string;
}

/**
 * Proto counterpart: vnis.v2.halonet.Reservation.
 * DTO counterpart: normalized ReservationDto.
 */
export interface Reservation {
  reservationId: string;
  svName: string;
  groundStation: GroundStationReference;
  timeWindow: TimeWindow;
  status: ReservationStatusType;
  prepassSeconds?: number;
  postpassSeconds?: number;
  configuration?: string;
}

/**
 * Proto counterpart: vnis.v2.halonet.SvId.
 * DTO counterpart: SpaceVehicleDto.
 */
export interface SpaceVehicleIdDto {
  svName: string;
}

/**
 * Proto counterpart: vnis.v2.halonet.SvId.
 * DTO counterpart: normalized SpaceVehicleIdDto.
 */
export interface SpaceVehicleId {
  svName: string;
}

/**
 * Proto counterpart: vnis.v2.common.TimestampedPositionAndVelocity.
 * DTO counterpart: OrbitPointDto.
 */
export interface OrbitPointDto {
  sourceTime: string;
  position: OrbitPosition;
}

/**
 * Proto counterpart: vnis.v2.common.TimestampedPositionAndVelocity.
 * DTO counterpart: normalized OrbitPointDto.
 */
export interface OrbitPoint {
  sourceTime: Date;
  position: GeoLocation;
}

/**
 * Proto counterpart: GetPropagatedStatesResponse.propagated_states grouped by SV.
 * DTO counterpart: Map<String, List<OrbitPointDto>> returned by SpaceVehicleController.
 */
export type OrbitPointsBySvNameDto = Record<string, OrbitPointDto[]>;

/**
 * Proto counterpart: GetPropagatedStatesResponse.propagated_states grouped by SV.
 * DTO counterpart: normalized Map<String, List<OrbitPointDto>>.
 */
export type OrbitPointsBySvName = Record<string, OrbitPoint[]>;

/**
 * The propagated state of a space vehicle at a single moment.
 * Field names match Terminal so that the value can be spread onto a terminal.
 */
export interface SpaceVehiclePosition {
  latitudeDeg: number;
  longitudeDeg: number;
  altitudeFeet: number;
  // Seconds since the epoch, to match Terminal.sourceTime.
  sourceTime: number;
  // True when the requested time is before the first point or after the last point.
  isOutsidePropagationWindow: boolean;
}

/**
 * The current propagated position of each space vehicle, keyed by svName.
 */
export type SpaceVehiclePositionsBySvName = Record<string, SpaceVehiclePosition>;
